import type { CashbackLedgerEntry, CashbackSummary } from '@erp/shared';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { Badge, Card, DataTable, EmptyState, FormField, Input, Skeleton } from '../../components/ui/index.js';
import { useAuthStore } from '../../lib/authStore.js';
import { getCashbackSummary, listCashbackLedger } from '../../lib/cashback.js';
import { formatDecimals } from '../../lib/money.js';
import { KpiCard } from './_shared/KpiCard.js';

const TONE: Record<string, 'success' | 'danger' | 'info' | 'neutral' | 'warning'> = {
  earn: 'success', redeem: 'info', adjust: 'warning', expire: 'neutral', reverse: 'danger',
};

/** First day of the current month, as YYYY-MM-DD — the default the shop asks about. */
function monthStart(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
}
function todayStr(): string {
  return new Date().toISOString().slice(0, 10);
}

interface PerCustomer {
  partyId: string;
  partyName: string;
  partyCode: string | null;
  earned: number;
  redeemed: number;
  net: number;
}

/**
 * Cashback as a REPORT rather than a list of movements.
 *
 * The ledger answers "what happened"; a shop owner asks two different questions
 * — what do we still owe customers, and what did this scheme cost us over a
 * period. The first is a running liability (a real balance on the books), the
 * second is a flow, and mixing them is how a scheme quietly becomes expensive:
 * the outstanding number can look calm while the monthly give-away climbs.
 *
 * Built on the endpoints that already exist — the summary for the liability, the
 * (now date-filterable) ledger for the period — rather than a new report query,
 * so this page can never disagree with the ledger it links to.
 */
export function CashbackReportPage(): JSX.Element {
  const { t, i18n } = useTranslation('cashback');
  const { t: tc } = useTranslation();
  const baseCurrency = useAuthStore((s) => s.tenant?.baseCurrencyCode) ?? 'USD';
  const money = (n: string | number): string => formatDecimals(String(n), baseCurrency, i18n.language);

  const [from, setFrom] = useState(monthStart);
  const [to, setTo] = useState(todayStr);
  const [summary, setSummary] = useState<CashbackSummary | null>(null);
  const [rows, setRows] = useState<CashbackLedgerEntry[] | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        const [s, r] = await Promise.all([
          getCashbackSummary(),
          listCashbackLedger({ ...(from ? { from } : {}), ...(to ? { to } : {}), limit: 1000 }),
        ]);
        setSummary(s);
        setRows(r);
      } catch { toast.error(tc('errors:server')); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [from, to]);

  // Per-type and per-customer totals for the chosen window. Derived here rather
  // than fetched: the rows on screen ARE the period, so the figures can never
  // describe a different set than the one the user is looking at.
  const { byType, perCustomer, earned, redeemed } = useMemo(() => {
    const types = new Map<string, number>();
    const people = new Map<string, PerCustomer>();
    let e = 0;
    let rd = 0;
    for (const r of rows ?? []) {
      const amt = Number(r.amount);
      types.set(r.type, (types.get(r.type) ?? 0) + amt);
      if (amt > 0) e += amt; else rd += amt;
      const cur = people.get(r.partyId) ?? {
        partyId: r.partyId, partyName: r.partyName, partyCode: r.partyCode, earned: 0, redeemed: 0, net: 0,
      };
      if (amt > 0) cur.earned += amt; else cur.redeemed += amt;
      cur.net += amt;
      people.set(r.partyId, cur);
    }
    return {
      byType: [...types.entries()].sort((a, b) => Math.abs(b[1]) - Math.abs(a[1])),
      // Biggest earners first — the customers the scheme is actually paying.
      perCustomer: [...people.values()].sort((a, b) => b.earned - a.earned),
      earned: e,
      redeemed: rd,
    };
  }, [rows]);

  if (!rows || !summary) {
    return <Card><Skeleton className="h-6 w-48" /><Skeleton className="mt-4 h-40 w-full" /></Card>;
  }

  const columns = [
    {
      id: 'customer', header: t('ledger.columns.customer', { defaultValue: 'Customer' }),
      cell: (r: PerCustomer) => (
        <div className="min-w-0">
          <div className="truncate">{r.partyName}</div>
          {r.partyCode ? <div className="font-mono text-xs text-fg-muted" dir="ltr">{r.partyCode}</div> : null}
        </div>
      ),
      searchValue: (r: PerCustomer) => `${r.partyName} ${r.partyCode ?? ''}`,
    },
    {
      id: 'earned', header: t('report.earned', { defaultValue: 'Earned' }), align: 'end' as const,
      cell: (r: PerCustomer) => <span className="font-mono text-success">{money(r.earned)}</span>,
      sortValue: (r: PerCustomer) => r.earned,
    },
    {
      id: 'redeemed', header: t('report.redeemed', { defaultValue: 'Redeemed' }), align: 'end' as const,
      cell: (r: PerCustomer) => <span className="font-mono text-danger">{money(r.redeemed)}</span>,
      sortValue: (r: PerCustomer) => r.redeemed,
    },
    {
      id: 'net', header: t('report.net', { defaultValue: 'Net' }), align: 'end' as const,
      cell: (r: PerCustomer) => <span className="font-mono font-semibold">{money(r.net)}</span>,
      sortValue: (r: PerCustomer) => r.net,
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      <header>
        <h1 className="text-2xl font-semibold">{t('report.title', { defaultValue: 'Cashback report' })}</h1>
        <p className="mt-1 text-sm text-fg-muted">
          {t('report.subtitle', { defaultValue: 'What the scheme still owes, and what it cost over a period.' })}
        </p>
      </header>

      <Card className="flex flex-wrap items-end gap-3">
        <FormField id="cb-r-from" label={t('ledger.from', { defaultValue: 'From' })}>
          <Input id="cb-r-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} max={to || undefined} />
        </FormField>
        <FormField id="cb-r-to" label={t('ledger.to', { defaultValue: 'To' })}>
          <Input id="cb-r-to" type="date" value={to} onChange={(e) => setTo(e.target.value)} min={from || undefined} />
        </FormField>
      </Card>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {/* The liability is ALL-TIME and deliberately not filtered by the dates:
            it is what the shop owes right now, and a date range cannot change that. */}
        <KpiCard
          label={t('report.outstanding', { defaultValue: 'Outstanding (all time)' })}
          value={money(summary.outstandingLiability)}
          hint={t('report.outstandingHint', { defaultValue: 'Balance customers can still spend' })}
        />
        <KpiCard label={t('report.earnedInPeriod', { defaultValue: 'Given in period' })} value={money(earned)} />
        <KpiCard label={t('report.redeemedInPeriod', { defaultValue: 'Spent in period' })} value={money(redeemed)} />
        <KpiCard
          label={t('report.customers', { defaultValue: 'Customers with a balance' })}
          value={String(summary.customers)}
        />
      </div>

      <Card>
        <h2 className="text-sm font-semibold text-fg-muted">{t('report.byType', { defaultValue: 'Movements in this period' })}</h2>
        {byType.length === 0 ? (
          <p className="mt-3 text-sm text-fg-muted">{t('report.noneInPeriod', { defaultValue: 'No cashback movement in this period.' })}</p>
        ) : (
          <div className="mt-3 flex flex-wrap gap-4">
            {byType.map(([type, total]) => (
              <div key={type} className="flex items-center gap-2">
                <Badge tone={TONE[type] ?? 'neutral'}>{t(`type.${type}`, { defaultValue: type })}</Badge>
                <span className="font-mono text-sm font-semibold">{money(total)}</span>
              </div>
            ))}
          </div>
        )}
      </Card>

      {perCustomer.length === 0 ? (
        <EmptyState title={t('report.noneInPeriod', { defaultValue: 'No cashback movement in this period.' })} />
      ) : (
        <Card className="p-0">
          <DataTable
            columns={columns}
            data={perCustomer}
            rowKey={(r) => r.partyId}
            searchPlaceholder={t('ledger.search', { defaultValue: 'Search…' })}
          />
        </Card>
      )}
    </div>
  );
}
